import { Component, Inject, Input, OnInit, PLATFORM_ID, WritableSignal, computed, inject, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { DeliveriesService } from './deliveries.service';
import { AccountService } from '../../../shared/account.service';
import { DeliveryOptionsResponseItem } from '../../../shared/types/account.types';
import { DeliveryInformation, DeliveryOptionsFormattedItem, UpdateDeliveryInfoPayload } from './intarfaces';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { tap } from 'rxjs';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SignalsStoreService } from '../../../shared/signals-store.service';
import { NotificationService } from '../../../shared/notification/notification.service';
import { EmptyMessageComponent } from '../../../shared/empty-message/empty-message.component';
import { OrderService } from '../../../shared/order.service';
import { ModalContentService } from '../../../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../../../shared/constants/modal-content-types';
import { SettingsService } from '../../settings.service';
import { afterDeliveryUpdates } from '../../../shared/common/utils';

@Component({
    selector: 'app-deliveries',
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatIcon,
        MatSelectModule,
        FormsModule,
        MatRadioModule,
        CommonModule,
        EmptyMessageComponent
    ],
    templateUrl: './deliveries.component.html',
    styleUrl: './deliveries.component.scss'
})
export class DeliveriesComponent implements OnInit {

  private deliveriesService = inject(DeliveriesService);
  private accountService = inject(AccountService);
  private signalsStoreService = inject(SignalsStoreService);
  #notificationService = inject(NotificationService);
  #orderService = inject(OrderService);
  #modalService = inject(ModalContentService);
  #settingsService = inject(SettingsService);
  private activeModal = inject(NgbActiveModal, { optional: true });

  @Input() inModal = false;

  isRequiredAddress = computed(() => this.signalsStoreService.isAddressRequired());

  deliveryZoneInfo = computed(() => this.deliveriesService.deliveryZoneInfo());

  selectedDeliveryDayAll: WritableSignal<DeliveryOptionsResponseItem | null> = signal(null);
  // DEV-9289: initialise to null (not -1) so the "unset" state matches what
  // the backend returns when no time window is stored — the shouldAffect
  // derive at submit time compares this against deliveryZoneInfo values, and
  // `-1 !== null` produced false positives that mis-flagged temporal changes
  // as permanent, triggering PREORDER_WINDOW_CONFLICT for orders that the
  // change wouldn't even touch.
  selectedDeliveryTimeWindowAll: WritableSignal<number | string | null> = signal(null);
  selectedDeliveryDay: WritableSignal<DeliveryOptionsResponseItem | null> = signal(null);
  selectedDeliveryTimeWindow: WritableSignal<number | string | null> = signal(null);
  isSaveChangesDisabled: WritableSignal<boolean> = signal(true);
  isTemporaryRouteChange = signal(false);
  temporaryRouteChangeMessage = signal('');
  deliveryOptions: DeliveryOptionsFormattedItem[] = [];

  // ELIO-6888: snapshot of the selection the backend actually managed to load
  // into the form. The "is this a permanent change?" derive compares against
  // THIS, never against the raw delivery-info payload: when the stored
  // permanent window can't be resolved inside the day's timeSet the signal
  // stays null, and diffing null against the stored id read a this-week-only
  // change as permanent, submitting the every-week section the customer never
  // touched.
  #initialPermanentDayId: string | number | null = null;
  #initialPermanentTimeWindowId: number | string | null = null;
  #initialOrderDayId: string | number | null = null;
  #initialOrderTimeWindowId: number | string | null = null;

  session = computed(() => this.signalsStoreService.sessionSignal());

  isWaitingForAPIResponse = computed(() => this.#settingsService.isWaitingForAPIResponse());
  submitButtonText = computed(() => this.#setUpSubmitButtonText())

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.deliveriesService.getDeliveryZoneInfo().pipe(
      tap((response) => {
        this.getDeliveryOptions(response.data);
        this.#validateTemporaryRoute(response.data);
      })
    ).subscribe();
  }

  #validateTemporaryRoute(deliveryInfo: DeliveryInformation) {
    const { temporaryChange } = deliveryInfo;
    this.isTemporaryRouteChange.set(!!temporaryChange?.active);

    if (this.isTemporaryRouteChange())
      this.temporaryRouteChangeMessage.set(temporaryChange?.message ?? '');
  }

  private getDeliveryOptions(deliveryInfo: DeliveryInformation) {
    this.accountService.getDeliveryOptions().subscribe({
      next: (options: DeliveryOptionsResponseItem[]) => {
        this.setUpDeliveryOptions(options, deliveryInfo);
      }
    });
  }

  private setUpDeliveryOptions(options: DeliveryOptionsResponseItem[], deliveryInfo: DeliveryInformation) {
    this.deliveryOptions = options;

    // Verify if the user has order delivery information:
    if (deliveryInfo.order?.deliveryDayId) {
      const item = this.deliveryOptions.find(d => d.id === deliveryInfo.order.deliveryDayId);
      if (item) {
        this.selectedDeliveryDay.set(item);
        // ELIO-6888: resolve through the same helper the radio handler uses so
        // the checked radio and the signal can never disagree.
        this.selectedDeliveryTimeWindow.set(this.#resolveTimeWindowForDay(item));
      }
    }

    // Verify if the user has delivery info:
    if (deliveryInfo.deliveryDayId) {
      const item = this.deliveryOptions.find(d => d.id === deliveryInfo.deliveryDayId);
      if (item) {
        this.selectedDeliveryDayAll.set(item);
        if (item.allowTimeWindow && deliveryInfo.deliveryTimeWindowId) {
          const timeSlot = item.timeSet.find(t => t.id === deliveryInfo.deliveryTimeWindowId);
          if (timeSlot) {
            this.selectedDeliveryTimeWindowAll.set(timeSlot.id);
          }
        }
      }
    }

    this.#captureInitialSelection();
  }

  private setUpSelectedOptions(options?: DeliveryOptionsResponseItem[]) {
    if (!options) options = this.deliveryOptions;
    const selectedDeliveryDay = options.find(e => e.id === this.deliveryZoneInfo()?.deliveryDayId);
    const selectedOrderDeliveryDay = options.find(e => e.id === this.deliveryZoneInfo()?.order?.deliveryDayId);
    if (selectedDeliveryDay) {
      this.selectedDeliveryDay.set(selectedOrderDeliveryDay || null);
      this.selectedDeliveryDayAll.set(selectedDeliveryDay)
      // ELIO-6888: resolve the order window inside the ORDER day's own timeSet.
      // Window ids are per day, so looking it up in the permanent day's timeSet
      // dropped the this-week selection right after a successful save.
      this.selectedDeliveryTimeWindow.set(
        selectedOrderDeliveryDay ? this.#resolveTimeWindowForDay(selectedOrderDeliveryDay) : null
      );
      const selectedDeliveryWindow = selectedDeliveryDay.timeSet.find(e => e.id === this.deliveryZoneInfo()?.deliveryTimeWindowId);
      if (selectedDeliveryWindow?.id) {
        this.selectedDeliveryTimeWindowAll.set(selectedDeliveryWindow?.id);
      }
    }

    this.#captureInitialSelection();
  }

  #captureInitialSelection() {
    this.#initialPermanentDayId = this.selectedDeliveryDayAll()?.id ?? null;
    this.#initialPermanentTimeWindowId = this.selectedDeliveryTimeWindowAll();
    this.#initialOrderDayId = this.selectedDeliveryDay()?.id ?? null;
    this.#initialOrderTimeWindowId = this.selectedDeliveryTimeWindow();
  }

  // ELIO-6888: keep the radio group and the signal in sync for the this-week
  // section. The template used to paint the stored order window as checked
  // while this signal was reset to null, so clicking that already-checked
  // radio fired no change event and the selection never reached the payload.
  #resolveTimeWindowForDay(item: DeliveryOptionsResponseItem): number | string | null {
    const timeSet = item.timeSet ?? [];
    // Days with a single implicit slot: adopt it, there is nothing to pick.
    if (!item.allowTimeWindow) return timeSet[0]?.id ?? null;
    const storedOrderTimeWindowId = this.deliveryZoneInfo()?.order?.deliveryTimeWindowId ?? null;
    if (timeSet.some(t => t.id === storedOrderTimeWindowId)) return storedOrderTimeWindowId;
    // Mirrors the old template fallback: -1 is the "no specific window" slot.
    return timeSet.some(t => t.id === -1) ? -1 : null;
  }

  setSelectedDeliveryDayAll(event: MatSelectChange) {
    const option = this.deliveryOptions.find(o => o.id === event.value);
    if (option) {
      this.selectedDeliveryDayAll.set(option);
      // Days without a time-window picker render as a read-only input in the
      // template; auto-adopt the single default slot so the payload carries a
      // real slot id and the shouldAffect derive doesn't false-positive.
      if (!option.allowTimeWindow && option.timeSet?.[0]?.id != null) {
        this.selectedDeliveryTimeWindowAll.set(option.timeSet[0].id);
      } else {
        this.selectedDeliveryTimeWindowAll.set(null);
      }
      this.isSaveChangesDisabled.set(this.disableSaveChangesButtons());
    }
  }

  setSelectedDeliveryTimeWindowAll(value: any) {
    this.selectedDeliveryTimeWindowAll.set(value);
    this.isSaveChangesDisabled.set(this.disableSaveChangesButtons());
  }

  setSelectedDeliveryDay(event: any, item: DeliveryOptionsFormattedItem) {
    if (item && event.target.value === 'on') {
      this.selectedDeliveryDay.set(item);
      // Days without a picker adopt their single implicit slot; days with one
      // keep the window the order already had whenever the new day still
      // offers it, so the checked radio always matches the stored signal.
      this.selectedDeliveryTimeWindow.set(this.#resolveTimeWindowForDay(item));
      this.isSaveChangesDisabled.set(this.disableSaveChangesButtons());
    }
  }

  setSelectedDeliveryTimeWindow(event: any, timeWindowId: number | string | null) {
    // ELIO-6888: `!= null` instead of a truthy check - a slot whose id is 0
    // could never be selected, leaving the save blocked on that day.
    if (timeWindowId != null && event.target.value === 'on') {
      this.selectedDeliveryTimeWindow.set(timeWindowId);
      this.isSaveChangesDisabled.set(this.disableSaveChangesButtons());
    }
  }

  updateDeliveryInfo() {

    if (this.isTemporaryRouteChange()) {
      return;
    }

    // ELIO-6888: a change is permanent only when the customer actually moved
    // the every-week controls, so diff the signals against the snapshot taken
    // right after load (#captureInitialSelection) instead of against the raw
    // delivery-info payload. Diffing against the payload made an unresolvable
    // stored window (an empty "Choose a time window") look like a permanent
    // edit and submit the section the customer never touched. Comparing a
    // signal against its own snapshot is exact, so the -1 normalisation and
    // the allowTimeWindow special-casing DEV-9289 needed are no longer
    // necessary: a value the form never loaded is null on both sides.
    const permanentDayChanged = (this.selectedDeliveryDayAll()?.id ?? null) !== this.#initialPermanentDayId;
    const permanentSlotChanged = this.selectedDeliveryTimeWindowAll() !== this.#initialPermanentTimeWindowId;
    const shouldAffectCustomerInfo = permanentDayChanged || permanentSlotChanged;

    // if ((shouldAffectCustomerInfo && !this.selectedDeliveryTimeWindowAll()) || (!shouldAffectCustomerInfo && !this.selectedDeliveryTimeWindow())) {
    //   this.#notificationService.show({ text: 'You should select a valid time slot', type: 'warning' });
    //   return;
    // }

    // ELIO-6888: never submit a day-less change. When delivery-info carries a
    // day the options catalog does not list, the day signal stays null and the
    // old payload sent `dayId: null` with shouldAffectCustomerInfo: true -
    // a silent permanent change the customer never asked for.
    const targetDayId = (shouldAffectCustomerInfo ? this.selectedDeliveryDayAll()?.id : this.selectedDeliveryDay()?.id) ?? null;
    if (targetDayId == null) {
      this.#notificationService.show({
        text: `You should select a valid ${this.isRequiredAddress() ? 'delivery' : 'pick up'} day`,
        type: 'warning'
      });
      return;
    }

    const payload: UpdateDeliveryInfoPayload = {
      dayId: targetDayId,
      timeWindowId: shouldAffectCustomerInfo ? this.selectedDeliveryTimeWindowAll() : this.selectedDeliveryTimeWindow(),
      shouldAffectCustomerInfo
    }

    this.deliveriesService.updateDeliveryInfo(payload).pipe(
      tap((response: any) => {
        // DEV-9289: backend can return a structured 400 with reason
        // "PREORDER_WINDOW_CONFLICT" when the new delivery day would push
        // one or more active pre-orders outside their product window.
        // Show a clear modal explaining the situation and directing the
        // customer to support (email is dynamic per company).
        if (response?.error && response?.data?.reason === 'PREORDER_WINDOW_CONFLICT') {
          const d = response.data;
          const items = (d.conflictingPreOrders || []).map((p: any) =>
            `<li><strong>${p.name}</strong> — available ${p.windowStart} to ${p.windowEnd}</li>`
          ).join('');
          const supportLine = d.supportEmail
            ? ` Please contact customer support at <strong>${d.supportEmail}</strong> to help resolve this.`
            : ' Please contact customer support to help resolve this.';
          this.#modalService.openModal(ModalContentTypes.CONFIRMATION, {
            title: "We can't change your delivery day right now",
            textContent: (
              `<p>You have active pre-order product(s) tied to your current delivery schedule:</p>` +
              `<ul>${items}</ul>` +
              `<p>Changing your delivery day would push these products outside their available window.</p>` +
              `<p>${supportLine}</p>`
            ),
            confirmButtonText: 'I understand',
          });
          return;
        }
        if (response?.error) {
          // Fallback: show any other error as a generic toast
          this.#notificationService.show({ text: response.message || 'Something went wrong', type: 'error' });
          return;
        }

        this.setUpSelectedOptions();
        this.isSaveChangesDisabled.set(true);

        afterDeliveryUpdates({
          orderCutoffExceeded: this.deliveryZoneInfo()?.orderCutoffExceeded ?? null,
          orderRescheduled: this.deliveryZoneInfo()?.orderRescheduled ?? null,
          deliveryZoneInfo: this.deliveryZoneInfo(),
          orderService: this.#orderService,
          modalService: this.#modalService,
          isUpdatingFromProfile: false
        });

        if (this.inModal && this.activeModal) {
          this.activeModal.close({ saved: true });
        }
      })
    ).subscribe();

  }

  disableSaveChangesButtons() {
    // ELIO-6888: compare against the loaded snapshot, not the raw payload, so
    // a window the form could not resolve neither leaves Save enabled with
    // nothing to save nor counts as a pending permanent edit.
    if ((this.selectedDeliveryDayAll()?.id ?? null) !== this.#initialPermanentDayId) return false;
    if (this.selectedDeliveryTimeWindowAll() !== this.#initialPermanentTimeWindowId) return false;
    if ((this.selectedDeliveryDay()?.id ?? null) !== this.#initialOrderDayId) return false;
    if (this.selectedDeliveryTimeWindow() !== this.#initialOrderTimeWindowId) return false;
    return true;
  }

  #setUpSubmitButtonText() {
    return this.isWaitingForAPIResponse() ? 'Updating...' : 'Save Changes'
  }

  selectFromParent(item: DeliveryOptionsFormattedItem) {
    document.getElementById(`firstDeliveryDate${item.id}`)?.click();
  }
}
